import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'

import type {
  Card as StudyCard,
  Class,
  Deck,
  DeckVisibility,
  Goal,
  GoalMetric,
  GoalMode,
  NewGoalInput,
} from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, Card, Input } from '../../components'
import { showToast } from '../../components/Toast'
import { listMyTeacherClasses } from '../../lib/classes.js'
import {
  DeckApiError,
  createCard,
  deleteCard,
  getDeck,
  listCards,
  reorderCards,
  updateCard,
  updateDeck,
} from '../../lib/decks.js'
import { createGoal, deleteGoal, listGoalsForClass } from '../../lib/goals.js'
import styles from './DeckEditorPage.module.css'

type SaveState = 'idle' | 'saving' | 'saved' | 'offline' | 'error'

interface PendingCard {
  term: string
  definition: string
}

interface EditorState {
  deck: Deck | null
  cards: StudyCard[]
  pendingTitle: string
  pendingDescription: string
  pendingSubject: string
  pendingVisibility: DeckVisibility
  pendingCards: Record<string, PendingCard>
  saveState: SaveState
  saveError: string | null
  loading: boolean
  errorMessage: string | null
}

interface DeckGoal {
  classInfo: Class
  goal: Goal
}

const INITIAL_STATE: EditorState = {
  deck: null,
  cards: [],
  pendingTitle: '',
  pendingDescription: '',
  pendingSubject: '',
  pendingVisibility: 'private',
  pendingCards: {},
  saveState: 'idle',
  saveError: null,
  loading: true,
  errorMessage: null,
}

function toPendingCards(cards: StudyCard[]): Record<string, PendingCard> {
  return Object.fromEntries(
    cards.map((card) => [card.id, { term: card.term, definition: card.definition }]),
  )
}

function errorText(err: unknown) {
  if (err instanceof DeckApiError || err instanceof Error) return err.message
  return 'Something went wrong — please try again.'
}

function saveStateText(saveState: SaveState, saveError: string | null) {
  if (saveState === 'saving') return 'Saving…'
  if (saveState === 'saved') return 'Saved just now'
  if (saveState === 'offline') return 'Offline — changes will save when you reconnect'
  if (saveState === 'error') return saveError ?? 'Save failed — retry'
  return 'All changes saved'
}

export default function DeckEditorPage(): JSX.Element {
  const { deckId } = useParams()
  const { uid, role } = useAuth()
  const [state, setState] = useState<EditorState>(INITIAL_STATE)
  const [teacherClasses, setTeacherClasses] = useState<Class[]>([])
  const [deckGoals, setDeckGoals] = useState<DeckGoal[]>([])
  const [goalsLoading, setGoalsLoading] = useState(false)
  const [goalStatus, setGoalStatus] = useState('')
  const [goalForm, setGoalForm] = useState({
    classId: '',
    title: '',
    mode: 'speedMatch' as GoalMode,
    targetMetric: 'accuracy' as GoalMetric,
    targetValue: '0.8',
    dueDate: '',
  })
  const goalDialogRef = useRef<HTMLDialogElement | null>(null)
  const addButtonRef = useRef<HTMLButtonElement | null>(null)
  const termRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const moveButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const loadDeck = useCallback(async () => {
    if (!deckId) return
    setState((previous) => ({ ...previous, loading: true, errorMessage: null }))

    try {
      const [deck, cards] = await Promise.all([getDeck(deckId), listCards(deckId)])
      if (!deck) {
        setState((previous) => ({
          ...previous,
          loading: false,
          errorMessage: 'This deck no longer exists.',
        }))
        return
      }
      setState({
        deck,
        cards,
        pendingTitle: deck.title,
        pendingDescription: deck.description,
        pendingSubject: deck.subject ?? '',
        pendingVisibility: deck.visibility,
        pendingCards: toPendingCards(cards),
        saveState: 'idle',
        saveError: null,
        loading: false,
        errorMessage: null,
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, loading: false, errorMessage: message }))
      showToast(message, 'error')
    }
  }, [deckId])

  useEffect(() => {
    void loadDeck()
  }, [loadDeck])

  const loadGoals = useCallback(async () => {
    if (!uid || !deckId) return
    setGoalsLoading(true)
    try {
      const classes = await listMyTeacherClasses(uid, role)
      setTeacherClasses(classes)
      const goalsByClass = await Promise.all(
        classes.map(async (classInfo) => ({
          classInfo,
          goals: await listGoalsForClass(classInfo.id),
        })),
      )
      setDeckGoals(
        goalsByClass.flatMap(({ classInfo, goals }) =>
          goals.filter((goal) => goal.deckId === deckId).map((goal) => ({ classInfo, goal })),
        ),
      )
    } catch (err) {
      const message = errorText(err)
      setGoalStatus(message)
      showToast(message, 'error')
    } finally {
      setGoalsLoading(false)
    }
  }, [deckId, role, uid])

  useEffect(() => {
    void loadGoals()
  }, [loadGoals])

  const dirtyDeckPatch = useMemo(() => {
    if (!state.deck) return null
    const patch: Partial<Pick<Deck, 'title' | 'description' | 'subject' | 'visibility'>> = {}
    if (state.pendingTitle !== state.deck.title) patch.title = state.pendingTitle
    if (state.pendingDescription !== state.deck.description) {
      patch.description = state.pendingDescription
    }
    const subject = state.pendingSubject.trim() ? state.pendingSubject.trim() : null
    if (subject !== state.deck.subject) patch.subject = subject
    if (state.pendingVisibility !== state.deck.visibility)
      patch.visibility = state.pendingVisibility
    return Object.keys(patch).length > 0 ? patch : null
  }, [
    state.deck,
    state.pendingDescription,
    state.pendingSubject,
    state.pendingTitle,
    state.pendingVisibility,
  ])

  const saveDeckFields = useCallback(async () => {
    if (!deckId || !state.deck || !dirtyDeckPatch) return
    if (!navigator.onLine) {
      setState((previous) => ({ ...previous, saveState: 'offline' }))
      return
    }

    setState((previous) => ({ ...previous, saveState: 'saving', saveError: null }))
    try {
      await updateDeck(deckId, dirtyDeckPatch)
      const updatedAt = new Date().toISOString()
      setState((previous) => ({
        ...previous,
        deck: previous.deck
          ? {
              ...previous.deck,
              ...dirtyDeckPatch,
              subject:
                dirtyDeckPatch.subject === undefined
                  ? previous.deck.subject
                  : dirtyDeckPatch.subject,
              updatedAt,
            }
          : previous.deck,
        saveState: 'saved',
      }))
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }, [deckId, dirtyDeckPatch, state.deck])

  const saveDirtyCards = useCallback(async () => {
    if (!deckId) return
    const dirtyCards = state.cards.filter((card) => {
      const pending = state.pendingCards[card.id]
      return pending && (pending.term !== card.term || pending.definition !== card.definition)
    })
    if (dirtyCards.length === 0) return
    if (!navigator.onLine) {
      setState((previous) => ({ ...previous, saveState: 'offline' }))
      return
    }

    setState((previous) => ({ ...previous, saveState: 'saving', saveError: null }))
    try {
      await Promise.all(
        dirtyCards.map((card) => {
          const pending = state.pendingCards[card.id]!
          return updateCard(deckId, card.id, pending)
        }),
      )
      setState((previous) => ({
        ...previous,
        cards: previous.cards.map((card) => {
          const pending = previous.pendingCards[card.id]
          return pending ? { ...card, ...pending } : card
        }),
        saveState: 'saved',
      }))
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }, [deckId, state.cards, state.pendingCards])

  useEffect(() => {
    if (!dirtyDeckPatch) return
    const timer = window.setTimeout(() => {
      void saveDeckFields()
    }, 800)
    return () => window.clearTimeout(timer)
  }, [dirtyDeckPatch, saveDeckFields])

  useEffect(() => {
    const hasDirtyCard = state.cards.some((card) => {
      const pending = state.pendingCards[card.id]
      return pending && (pending.term !== card.term || pending.definition !== card.definition)
    })
    if (!hasDirtyCard) return
    const timer = window.setTimeout(() => {
      void saveDirtyCards()
    }, 800)
    return () => window.clearTimeout(timer)
  }, [saveDirtyCards, state.cards, state.pendingCards])

  useEffect(() => {
    if (state.saveState !== 'saved') return
    const timer = window.setTimeout(() => {
      setState((previous) =>
        previous.saveState === 'saved' ? { ...previous, saveState: 'idle' } : previous,
      )
    }, 3000)
    return () => window.clearTimeout(timer)
  }, [state.saveState])

  function updatePendingCard(cardId: string, patch: PendingCard) {
    setState((previous) => ({
      ...previous,
      pendingCards: {
        ...previous.pendingCards,
        [cardId]: patch,
      },
    }))
  }

  async function handleAddCard() {
    if (!deckId || state.cards.length >= 490) return
    try {
      const card = await createCard(deckId, { term: '', definition: '' }, state.cards.length)
      setState((previous) => ({
        ...previous,
        deck: previous.deck
          ? {
              ...previous.deck,
              cardCount: previous.cards.length + 1,
              updatedAt: new Date().toISOString(),
            }
          : previous.deck,
        cards: [...previous.cards, card],
        pendingCards: {
          ...previous.pendingCards,
          [card.id]: { term: card.term, definition: card.definition },
        },
        saveState: 'saved',
      }))
      window.requestAnimationFrame(() => {
        termRefs.current[card.id]?.focus()
        termRefs.current[card.id]?.scrollIntoView({ block: 'center' })
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }

  async function handleDeleteCard(cardId: string) {
    if (!deckId || !window.confirm('Delete this card?')) return
    const cardIndex = state.cards.findIndex((card) => card.id === cardId)
    try {
      await deleteCard(deckId, cardId, state.cards.length)
      setState((previous) => {
        const cards = previous.cards.filter((card) => card.id !== cardId)
        const pendingCards = { ...previous.pendingCards }
        delete pendingCards[cardId]
        return {
          ...previous,
          deck: previous.deck
            ? { ...previous.deck, cardCount: cards.length, updatedAt: new Date().toISOString() }
            : previous.deck,
          cards,
          pendingCards,
          saveState: 'saved',
        }
      })
      window.requestAnimationFrame(() => {
        const nextCard = state.cards[cardIndex + 1] ?? state.cards[cardIndex - 1]
        if (nextCard && nextCard.id !== cardId) {
          termRefs.current[nextCard.id]?.focus()
        } else {
          addButtonRef.current?.focus()
        }
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }

  async function handleMoveCard(cardId: string, direction: -1 | 1) {
    if (!deckId) return
    const index = state.cards.findIndex((card) => card.id === cardId)
    const nextIndex = index + direction
    if (index < 0 || nextIndex < 0 || nextIndex >= state.cards.length) return

    const reordered = [...state.cards]
    const [moved] = reordered.splice(index, 1)
    if (!moved) return
    reordered.splice(nextIndex, 0, moved)
    const positioned = reordered.map((card, position) => ({ ...card, position: position + 1 }))

    setState((previous) => ({ ...previous, cards: positioned, saveState: 'saving' }))
    try {
      await reorderCards(
        deckId,
        positioned.map((card) => card.id),
      )
      setState((previous) => ({
        ...previous,
        cards: positioned,
        deck: previous.deck ? { ...previous.deck, updatedAt: new Date().toISOString() } : null,
        saveState: 'saved',
      }))
      window.requestAnimationFrame(() => {
        moveButtonRefs.current[`${cardId}:${direction === -1 ? 'up' : 'down'}`]?.focus()
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
      void loadDeck()
    }
  }

  function handleRetrySave() {
    void saveDeckFields()
    void saveDirtyCards()
  }

  function openGoalDialog() {
    const today = new Date().toISOString().slice(0, 10)
    setGoalForm((previous) => ({
      ...previous,
      classId: previous.classId || teacherClasses[0]?.id || '',
      dueDate: previous.dueDate || today,
    }))
    goalDialogRef.current?.showModal()
  }

  async function handleCreateGoal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!uid || !deckId) return
    const input: NewGoalInput = {
      deckId,
      title: goalForm.title.trim(),
      mode: goalForm.mode,
      targetMetric: goalForm.targetMetric,
      targetValue: Number(goalForm.targetValue),
      dueAt: `${goalForm.dueDate}T23:59:59.000Z`,
    }
    setGoalStatus('Saving goal…')
    try {
      await createGoal(goalForm.classId, input)
      goalDialogRef.current?.close()
      setGoalForm((previous) => ({ ...previous, title: '' }))
      setGoalStatus('Saved')
      showToast('Goal created.', 'success')
      await loadGoals()
    } catch (err) {
      const message = errorText(err)
      setGoalStatus(message)
      showToast(message, 'error')
    }
  }

  async function handleDeleteGoal(classId: string, goalId: string) {
    if (!window.confirm('Delete this goal?')) return
    setGoalStatus('Deleting goal…')
    try {
      await deleteGoal(classId, goalId)
      setGoalStatus('Deleted')
      showToast('Goal deleted.', 'success')
      await loadGoals()
    } catch (err) {
      const message = errorText(err)
      setGoalStatus(message)
      showToast(message, 'error')
    }
  }

  const addDisabled = state.cards.length >= 490

  if (state.loading) {
    return (
      <section className={styles.page}>
        <p role="status" aria-live="polite">
          Loading deck…
        </p>
      </section>
    )
  }

  if (state.errorMessage || !state.deck) {
    return (
      <section className={styles.page}>
        <div className={styles.errorBlock}>
          <p role="alert">{state.errorMessage ?? 'This deck no longer exists.'}</p>
          <Button type="button" variant="secondary" onClick={() => void loadDeck()}>
            Retry
          </Button>
          <Link to="/teacher/decks">Back to decks</Link>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Deck editor</p>
          <h2>{state.deck.title || 'Untitled deck'}</h2>
        </div>
        <Link className={styles.backLink} to="/teacher/decks">
          Back to decks
        </Link>
      </div>

      <Card className={styles.editorCard} elevated>
        <div className={styles.formGrid}>
          <Input
            label="Title"
            value={state.pendingTitle}
            onChange={(event) =>
              setState((previous) => ({ ...previous, pendingTitle: event.target.value }))
            }
          />
          <label className={styles.textareaField}>
            <span>Description</span>
            <textarea
              rows={3}
              value={state.pendingDescription}
              onChange={(event) =>
                setState((previous) => ({
                  ...previous,
                  pendingDescription: event.target.value,
                }))
              }
            />
          </label>
          <Input
            label="Subject"
            value={state.pendingSubject}
            onChange={(event) =>
              setState((previous) => ({ ...previous, pendingSubject: event.target.value }))
            }
            placeholder="Optional"
          />
          <fieldset className={styles.visibilityGroup}>
            <legend>Visibility</legend>
            <label>
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={state.pendingVisibility === 'private'}
                onChange={() =>
                  setState((previous) => ({ ...previous, pendingVisibility: 'private' }))
                }
              />
              Private
            </label>
            <label>
              <input
                type="radio"
                name="visibility"
                value="class"
                checked={state.pendingVisibility === 'class'}
                disabled
                aria-describedby="class-visibility-help"
              />
              Class
            </label>
            <span id="class-visibility-help" className={styles.radioHelp}>
              Class-only deck publishing is deferred; use domain visibility for pilot goals.
            </span>
            <label>
              <input
                type="radio"
                name="visibility"
                value="domain"
                checked={state.pendingVisibility === 'domain'}
                onChange={() =>
                  setState((previous) => ({ ...previous, pendingVisibility: 'domain' }))
                }
              />
              Domain
            </label>
          </fieldset>
        </div>
        <div className={styles.saveRow}>
          <p role="status" aria-live="polite" aria-atomic="true" className={styles.saveState}>
            {state.saveState === 'saved' ? <span aria-hidden="true">✓ </span> : null}
            {saveStateText(state.saveState, state.saveError)}
          </p>
          {state.saveState === 'error' ? (
            <Button type="button" variant="secondary" size="sm" onClick={handleRetrySave}>
              Retry
            </Button>
          ) : null}
        </div>
      </Card>

      <div className={styles.cardsHeader}>
        <div>
          <h3>Cards</h3>
          <p className={styles.muted}>
            {state.cards.length} card{state.cards.length === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <ol className={styles.cardList}>
        {state.cards.map((card, index) => {
          const pending = state.pendingCards[card.id] ?? {
            term: card.term,
            definition: card.definition,
          }
          return (
            <li key={card.id} className={styles.cardItem}>
              <span className={styles.cardNumber} aria-hidden="true">
                {index + 1}.
              </span>
              <div className={styles.reorderControls}>
                <button
                  type="button"
                  ref={(node) => {
                    moveButtonRefs.current[`${card.id}:up`] = node
                  }}
                  className={styles.iconButton}
                  aria-label={`Move card ${index + 1} up`}
                  disabled={index === 0}
                  onClick={() => void handleMoveCard(card.id, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  ref={(node) => {
                    moveButtonRefs.current[`${card.id}:down`] = node
                  }}
                  className={styles.iconButton}
                  aria-label={`Move card ${index + 1} down`}
                  disabled={index === state.cards.length - 1}
                  onClick={() => void handleMoveCard(card.id, 1)}
                >
                  ↓
                </button>
              </div>
              <div className={styles.cardFields}>
                <label className={styles.textareaField}>
                  <span>Term</span>
                  <textarea
                    ref={(node) => {
                      termRefs.current[card.id] = node
                    }}
                    rows={2}
                    value={pending.term}
                    onChange={(event) =>
                      updatePendingCard(card.id, {
                        ...pending,
                        term: event.target.value,
                      })
                    }
                  />
                </label>
                <label className={styles.textareaField}>
                  <span>Definition</span>
                  <textarea
                    rows={3}
                    value={pending.definition}
                    onChange={(event) =>
                      updatePendingCard(card.id, {
                        ...pending,
                        definition: event.target.value,
                      })
                    }
                  />
                </label>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleDeleteCard(card.id)}
              >
                Delete card
              </Button>
            </li>
          )
        })}
      </ol>

      <div className={styles.addCardRow}>
        <Button
          ref={addButtonRef}
          type="button"
          variant="primary"
          onClick={() => void handleAddCard()}
          disabled={addDisabled}
        >
          Add card
        </Button>
        {addDisabled ? <p className={styles.muted}>Decks are capped at 490 cards.</p> : null}
      </div>

      <section className={styles.goalsSection} aria-labelledby="goals-title">
        <div className={styles.cardsHeader}>
          <div>
            <h3 id="goals-title">Goals</h3>
            <p className={styles.muted}>
              Assign this deck to a class. Students in that class who can see this deck will see the
              goal on their dashboard.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={openGoalDialog}
            disabled={teacherClasses.length === 0}
          >
            New goal
          </Button>
        </div>
        <p role="status" aria-live="polite" aria-atomic="true" className={styles.muted}>
          {goalsLoading ? 'Loading goals…' : goalStatus}
        </p>
        {deckGoals.length === 0 ? (
          <div className={styles.goalEmpty}>
            <p>No goals on this deck yet.</p>
          </div>
        ) : (
          <div className={styles.goalList}>
            {deckGoals.map(({ classInfo, goal }) => (
              <article className={styles.goalRow} key={`${classInfo.id}:${goal.id}`}>
                <div>
                  <h4>{goal.title}</h4>
                  <p className={styles.muted}>
                    {classInfo.name} · {goal.mode === 'speedMatch' ? 'Speed Match' : 'Flashcards'} ·{' '}
                    {goal.targetMetric} {goal.targetValue} · Due{' '}
                    {new Date(goal.dueAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => void handleDeleteGoal(classInfo.id, goal.id)}
                >
                  Delete
                </Button>
              </article>
            ))}
          </div>
        )}
      </section>

      <dialog ref={goalDialogRef} className={styles.goalDialog} aria-labelledby="new-goal-title">
        <form
          method="dialog"
          className={styles.goalForm}
          onSubmit={(event) => void handleCreateGoal(event)}
        >
          <div>
            <h3 id="new-goal-title">New goal</h3>
            <p className={styles.muted}>
              This goal will appear for students in the selected class who can already see this
              deck.
            </p>
          </div>
          <label>
            <span>Class</span>
            <select
              required
              value={goalForm.classId}
              onChange={(event) =>
                setGoalForm((previous) => ({ ...previous, classId: event.target.value }))
              }
            >
              <option value="" disabled>
                Choose a class
              </option>
              {teacherClasses.map((classInfo) => (
                <option key={classInfo.id} value={classInfo.id}>
                  {classInfo.name}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Title"
            required
            maxLength={80}
            value={goalForm.title}
            onChange={(event) =>
              setGoalForm((previous) => ({ ...previous, title: event.target.value }))
            }
          />
          <div className={styles.goalFormGrid}>
            <label>
              <span>Mode</span>
              <select
                value={goalForm.mode}
                onChange={(event) =>
                  setGoalForm((previous) => ({
                    ...previous,
                    mode: event.target.value as GoalMode,
                  }))
                }
              >
                <option value="flashcards">Flashcards</option>
                <option value="speedMatch">Speed Match</option>
              </select>
            </label>
            <label>
              <span>Metric</span>
              <select
                value={goalForm.targetMetric}
                onChange={(event) => {
                  const targetMetric = event.target.value as GoalMetric
                  setGoalForm((previous) => ({
                    ...previous,
                    targetMetric,
                    targetValue: targetMetric === 'accuracy' ? '0.8' : '1',
                  }))
                }}
              >
                <option value="accuracy">Accuracy</option>
                <option value="completions">Completions</option>
                <option value="minutesStudied">Minutes studied</option>
              </select>
            </label>
          </div>
          <p className={styles.muted}>
            Accuracy is 0–1, completions are sessions, minutes studied are minutes.
          </p>
          <div className={styles.goalFormGrid}>
            <Input
              label="Target"
              type="number"
              required
              min={goalForm.targetMetric === 'accuracy' ? 0.01 : 1}
              max={
                goalForm.targetMetric === 'accuracy'
                  ? 1
                  : goalForm.targetMetric === 'completions'
                    ? 1000
                    : 600
              }
              step={goalForm.targetMetric === 'accuracy' ? 0.01 : 1}
              value={goalForm.targetValue}
              onChange={(event) =>
                setGoalForm((previous) => ({ ...previous, targetValue: event.target.value }))
              }
            />
            <Input
              label="Due date"
              type="date"
              required
              min={new Date().toISOString().slice(0, 10)}
              value={goalForm.dueDate}
              onChange={(event) =>
                setGoalForm((previous) => ({ ...previous, dueDate: event.target.value }))
              }
            />
          </div>
          <div className={styles.dialogActions}>
            <Button type="button" variant="ghost" onClick={() => goalDialogRef.current?.close()}>
              Cancel
            </Button>
            <Button type="submit">Create goal</Button>
          </div>
        </form>
      </dialog>
    </section>
  )
}
